/**
 * Map configuration for Lyktan.
 *
 * Central place for tile sources and initial view.
 * The map view (`src/lib/MapView.svelte`) is the only consumer.
 *
 * Hard rules from CLAUDE.md and UI-kontrakt vy 7 (Lyktan ritvy):
 * - No external beacons besides Protomaps.
 * - No tracker pixels, no analytics from the client.
 * - API keys are read from build-time env (Vite import.meta.env), never
 *   hardcoded or persisted in the browser.
 */

/**
 * Initial map center: Hisings Backa, Göteborg.
 * Coordinates rounded to four decimals (~11 m precision) — sufficient for
 * a city-block-level starting view, not so precise that it implies a
 * specific address.
 */
export const INITIAL_CENTER: { lng: number; lat: number } = {
  lng: 11.948,
  lat: 57.738,
};

/**
 * Initial zoom level. 13 gives roughly the full Hisings Backa neighbourhood
 * in a typical desktop viewport, matching the story acceptance ("rimlig
 * zoom ~13"). The story copy says ~13; the inline UI-kontrakt example
 * says 14. We default to 13 — pan/zoom covers the rest.
 */
export const INITIAL_ZOOM = 13;

/**
 * Read the Protomaps API key from build-time env. Returns `null` when the
 * key is missing or empty, so callers can render a localised error message
 * instead of letting MapLibre fail with an opaque 401.
 *
 * The `env` parameter is typed loosely so unit tests can pass a stub
 * without depending on Vite's ambient `ImportMetaEnv` interface.
 */
export interface MapEnv {
  VITE_PROTOMAPS_API_KEY?: string | undefined;
}

export function readProtomapsApiKey(env: MapEnv = import.meta.env as MapEnv): string | null {
  const raw = env.VITE_PROTOMAPS_API_KEY;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  return trimmed;
}

/**
 * Build the Protomaps vector tile URL template for MapLibre.
 *
 * Protomaps hosted API serves Mapbox Vector Tiles at
 *   `https://api.protomaps.com/tiles/v4/{z}/{x}/{y}.mvt?key=<API_KEY>`
 * Reference: https://docs.protomaps.com/deploy/protomaps-api
 *
 * MapLibre substitutes `{z}/{x}/{y}` automatically. The key is appended as
 * a query parameter — Protomaps does not currently support header auth on
 * their hosted endpoint, so the key will be visible in network requests
 * (this is acceptable: it is a domain-locked publishable key, not a
 * secret).
 */
export function buildProtomapsTileUrl(apiKey: string): string {
  return `https://api.protomaps.com/tiles/v4/{z}/{x}/{y}.mvt?key=${encodeURIComponent(apiKey)}`;
}

/**
 * Localised attribution snippet shown in the map's bottom-right.
 * Required by both Protomaps ToS and OSM (whose data Protomaps' base
 * layer derives from).
 */
export const ATTRIBUTION_VECTOR =
  '&copy; <a href="https://protomaps.com">Protomaps</a> &copy; ' +
  '<a href="https://openstreetmap.org">OpenStreetMap</a>';
